import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { api, formatApiErrorDetail } from '@/lib/api';
import PageMeta from '@/components/PageMeta';
import { FORMS } from '@/constants/testIds';

const TOPICS = [
  'General enquiry',
  'Early access',
  'Partnership',
  'Allied health / clinical',
  'Research',
  'Media',
  'Support',
  'Privacy',
  'Other',
];

export default function Contact() {
  const T = FORMS.contact;
  const [form, setForm] = useState({
    name: '', email: '', organisation: '', topic: TOPICS[0], message: '', consent: false,
  });
  const [state, setState] = useState({ submitting: false, success: false, error: '' });

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    if (state.submitting) return;
    setState({ submitting: true, success: false, error: '' });
    try {
      await api.post('/contact', form);
      setState({ submitting: false, success: true, error: '' });
      setForm({ name: '', email: '', organisation: '', topic: TOPICS[0], message: '', consent: false });
    } catch (err) {
      setState({
        submitting: false,
        success: false,
        error: formatApiErrorDetail(err.response?.data?.detail) || 'Submission failed. Please try again.',
      });
    }
  }

  return (
    <div>
      <PageMeta
        title="Contact"
        description="Get in touch with Valanti Labs — for partnerships, professional enquiries, support, media or research."
      />
      <section className="vl-container py-16 lg:py-24 max-w-3xl">
        <p className="mono-label text-vl-violet">Contact</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-vl-ink">
          We'd value an honest conversation.
        </h1>
        <p className="mt-6 text-lg text-vl-ink/70 leading-relaxed">
          For partnership, professional, research, media, support and privacy enquiries — please use the form below.
          We aim to respond within a small number of business days.
        </p>
      </section>

      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-16 lg:py-20 max-w-3xl">
          {state.success ? (
            <div data-testid={T.success} className="rounded-3xl bg-vl-surface border hairline p-10 text-center">
              <CheckCircle2 className="mx-auto text-[#4C7C59]" size={36} />
              <h2 className="font-serif-display text-3xl text-vl-ink mt-4">Thank you.</h2>
              <p className="mt-4 text-vl-ink/75 leading-relaxed max-w-lg mx-auto">
                Your message has been received. A member of the Valanti Labs team will be in touch.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid={T.form}
              className="rounded-3xl bg-vl-surface border hairline p-7 sm:p-10 space-y-6"
            >
              <Field label="Your name" required>
                <input data-testid={T.name} value={form.name} onChange={set('name')} required className={inputCls} />
              </Field>
              <Field label="Email" required>
                <input type="email" data-testid={T.email} value={form.email} onChange={set('email')} required className={inputCls} />
              </Field>
              <Field label="Organisation (optional)">
                <input data-testid={T.organisation} value={form.organisation} onChange={set('organisation')} className={inputCls} />
              </Field>
              <Field label="Topic" required>
                <select data-testid={T.topic} value={form.topic} onChange={set('topic')} required className={inputCls}>
                  {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Message" required>
                <textarea
                  data-testid={T.message}
                  value={form.message}
                  onChange={set('message')}
                  required
                  rows={6}
                  className={inputCls + ' resize-none'}
                />
              </Field>

              <label className="flex items-start gap-3 text-sm text-vl-ink/75 leading-relaxed">
                <input
                  type="checkbox"
                  data-testid={T.consent}
                  checked={form.consent}
                  onChange={set('consent')}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  I consent to Valanti Labs storing the information above so that we can respond to my enquiry. I will
                  not include sensitive health information in this message.
                </span>
              </label>

              {state.error && (
                <p data-testid={T.error} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {state.error}
                </p>
              )}

              <button
                type="submit"
                data-testid={T.submit}
                disabled={!form.consent || state.submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-full bg-vl-ink text-vl-bg px-7 py-3.5 text-sm font-medium hover:bg-vl-violet transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {state.submitting ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls =
  'w-full rounded-xl border border-vl-ink/15 bg-transparent px-4 py-3 text-vl-ink placeholder:text-vl-ink/40 focus:outline-none focus:border-vl-violet focus:ring-1 focus:ring-vl-violet transition-colors';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-vl-ink mb-2">
        {label} {required && <span className="text-vl-violet">*</span>}
      </span>
      {children}
    </label>
  );
}
